import { useEffect, useState, useRef, useMemo } from 'react';
import * as submissionQueue from '../utils/submissionQueue';

/**
 * useTelemetry Hook
 * Manages continuous telemetry heartbeat to Octacle API
 *
 * @param {Array} serviceDesks - Array of service desk objects [{id, token}, ...]
 * @param {Object} config - App config with optional heartbeat_interval_ms
 * @param {Object} [extras] - Extra heartbeat fields the caller wants pushed
 *                            every tick (e.g. ref_validation_status). Read live
 *                            from a ref so changes don't tear down the loop.
 * @returns {Object} { hasError, isRunning }
 */
export function useTelemetry(serviceDesks = [], config = {}, extras = {}) {
  // Read extras through a ref so callers can pass a fresh object each render
  // without restarting the heartbeat interval.
  const extrasRef = useRef(extras);
  extrasRef.current = extras;

  const [telemetryState, setTelemetryState] = useState({
    hasError: false,
    isRunning: false,
  });

  const intervalRef = useRef(null);
  const isVisibleRef = useRef(true);
  const hasLoggedRef = useRef(false); // Track if we've already logged initialization

  // Extract primitive values to use as stable dependencies
  const serviceDeskCount = serviceDesks?.length || 0;
  const token = serviceDesks?.[0]?.token || null;
  const deviceId = serviceDesks?.[0]?.id || null;
  const intervalMs = config?.heartbeat_interval_ms || 2 * 60 * 1000;

  // Validate serviceDesks and determine error state
  const hasError = useMemo(
    () =>
      serviceDesks &&
      (serviceDesks.length > 1 ||
        (serviceDesks.length === 1 && (!serviceDesks[0].token || !serviceDesks[0].id))),
    [serviceDeskCount, token, deviceId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    // Set error state if validation fails
    if (hasError) {
      setTelemetryState({ hasError: true, isRunning: false });
      console.error('[useTelemetry] ❌ Invalid serviceDesks configuration');
      return;
    }

    // Skip telemetry if no serviceDesks
    if (serviceDeskCount === 0) {
      setTelemetryState({ hasError: false, isRunning: false });
      // Only log once to avoid spam
      if (!hasLoggedRef.current) {
        console.log('[useTelemetry] ⏭️  No serviceDesks, skipping telemetry');
        hasLoggedRef.current = true;
      }
      return;
    }

    // Reset flag when telemetry starts
    hasLoggedRef.current = false;

    // Check if running in Android kiosk app
    const isInKioskMode = typeof window !== 'undefined' && typeof window.AndroidDevice !== 'undefined';

    // Skip telemetry if NOT in kiosk mode
    if (!isInKioskMode) {
      setTelemetryState({ hasError: false, isRunning: false });
      console.log('[useTelemetry] ⏭️  Not in kiosk mode, skipping telemetry');
      return;
    }

    // KIOSK-01: Pass device token to native ConnectivityTracker for connectivity telemetry
    if (token && window.AndroidDevice?.setDeviceToken) {
      try {
        window.AndroidDevice.setDeviceToken(token);
        console.log('[useTelemetry] ✅ Device token passed to native ConnectivityTracker');
      } catch (e) {
        console.warn('[useTelemetry] Failed to set device token:', e);
      }
    }

    // Get Android device info
    let androidInfo = null;
    try {
      androidInfo = JSON.parse(window.AndroidDevice.getDeviceInfo());
    } catch (e) {
      console.warn('[useTelemetry] Failed to parse Android device info:', e);
    }

    // Route through feedback-server so the kiosk WebView — which may be
    // loaded from appassets.androidplatform.net (bundled build) — does not
    // issue cross-origin POSTs directly to octacle.myhub.co.in.
    const apiBase = (config?.api_base || process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
    const attributesUrl = `${apiBase}/api/tb/${token}/attributes`;
    const telemetryUrl = `${apiBase}/api/tb/${token}/telemetry`;

    // Helper to filter out unavailable values
    const validValue = (v) => v && v !== 'permission_denied' && v !== 'unavailable' && v !== '02:00:00:00:00:00';

    // Pull network identifiers via individual bridge methods — these
    // aren't bundled into getDeviceInfo() because they change if the
    // tablet hops gateways. Read once at mount; if the kiosk
    // ever reconnects to a different WiFi the page watchdog will
    // reload and re-capture.
    const readBridge = (fn) => {
      try {
        return typeof window.AndroidDevice?.[fn] === 'function'
          ? window.AndroidDevice[fn]()
          : null;
      } catch (e) {
        return null;
      }
    };
    const wifiSsid = readBridge('getWifiSSID');
    const ipAddress = readBridge('getIpAddress');
    const macAddress = readBridge('getMacAddress');

    // Static device info - sent once as attributes (skip unavailable values)
    const attributesPayload = {
      android_id: androidInfo?.androidId,
      device_model: androidInfo?.model,
      device_manufacturer: androidInfo?.manufacturer,
      device_brand: androidInfo?.brand,
      android_version: androidInfo?.androidVersion,
      sdk_version: androidInfo?.sdkInt,
      app_version: androidInfo?.appVersionName,
      ...(validValue(androidInfo?.serial) && { serial_number: androidInfo.serial }),
      ...(validValue(wifiSsid) && { wifi_ssid: wifiSsid }),
      ...(validValue(ipAddress) && { ip_address: ipAddress }),
      ...(validValue(macAddress) && { mac_address: macAddress }),
    };

    // Heartbeat - sent periodically as telemetry. Battery + dynamic
    // health metrics are re-read inside sendTelemetry() so every tick
    // captures fresh values.
    const telemetryPayload = {
      is_active: 1,
    };

    console.log('[useTelemetry] ✅ Starting (kiosk mode)', {
      deviceId,
      intervalMs,
      androidDevice: androidInfo?.model,
      androidId: androidInfo?.androidId,
    });

    // Send attributes once on startup
    const sendAttributes = async () => {
      try {
        console.log('[useTelemetry] ➜ POST attributes', attributesUrl);
        const response = await fetch(attributesUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(attributesPayload),
          signal: AbortSignal.timeout(5000),
        });
        if (response.ok) {
          console.log('[useTelemetry] ⇦ Attributes sent successfully');
        } else {
          console.error('[useTelemetry] ✗ Attributes HTTP', response.status);
        }
      } catch (err) {
        console.error('[useTelemetry] ✗ Error sending attributes:', err?.message || err);
      }
    };

    // Send attributes immediately (once)
    sendAttributes();

    // Function to send telemetry
    const sendTelemetry = async () => {
      if (!isVisibleRef.current) {
        console.log('[useTelemetry] ⏸️  Page hidden, skipping telemetry send');
        return;
      }

      // Per-tick health snapshot: battery + signal + uptime + free memory.
      // Every value nullable — if the bridge method isn't present (older
      // APK on a tablet we haven't upgraded yet) the field is simply
      // omitted from the payload.
      let batteryLevel = null;
      let isCharging = null;
      let signalDbm = null;
      let uptimeS = null;
      let freeMemMb = null;
      try {
        if (window.AndroidDevice?.getBatteryLevel) {
          batteryLevel = window.AndroidDevice.getBatteryLevel();
        }
        if (window.AndroidDevice?.isBatteryCharging) {
          isCharging = window.AndroidDevice.isBatteryCharging();
        }
        if (window.AndroidDevice?.getSignalStrengthDbm) {
          signalDbm = window.AndroidDevice.getSignalStrengthDbm();
        }
        if (window.AndroidDevice?.getUptimeSeconds) {
          uptimeS = window.AndroidDevice.getUptimeSeconds();
        }
        if (window.AndroidDevice?.getFreeMemoryMb) {
          freeMemMb = window.AndroidDevice.getFreeMemoryMb();
        }
      } catch (e) {
        console.warn('[useTelemetry] Failed to get device health:', e);
      }

      // Build telemetry payload — omit any value that's null or
      // the "unavailable" sentinel (Integer.MIN_VALUE from native).
      // Live signals about kiosk health beyond the device itself:
      // - submission_queue_depth: items pending retry in localStorage. >0 means
      //   feedback couldn't reach the server on first try; growing means it's
      //   still failing. Best single number for fleet alarms.
      // - ref_validation_status: caller-supplied (via extras), reflects whether
      //   /api/ref/validate is currently OK from this kiosk.
      const queueDepth = submissionQueue.size();
      const liveExtras = extrasRef.current || {};

      const payload = {
        ...telemetryPayload,
        ...(batteryLevel !== null && batteryLevel >= 0 && { battery_level: batteryLevel }),
        ...(isCharging !== null && { is_charging: isCharging ? 1 : 0 }),
        ...(signalDbm !== null && signalDbm > -200 && { signal_strength_dbm: signalDbm }),
        ...(uptimeS !== null && uptimeS >= 0 && { uptime_s: uptimeS }),
        ...(freeMemMb !== null && freeMemMb >= 0 && { free_memory_mb: freeMemMb }),
        submission_queue_depth: queueDepth,
        ...liveExtras,
      };

      try {
        console.log('[useTelemetry] ➜ POST', telemetryUrl, payload);
        const response = await fetch(telemetryUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: AbortSignal.timeout(5000), // 5 second timeout
        });

        if (!response.ok) {
          console.error('[useTelemetry] ✗ HTTP', response.status, response.statusText);
          return;
        }

        console.log('[useTelemetry] ⇦ Success', response.status);

        // Tell the kiosk shell the React leg is alive. MainActivity keeps
        // `lastReactTelemetryAtMs` from this call and NOTHING else, and two
        // features are gated on it being non-zero:
        //   • the `web_offline` telemetry key — omitted entirely while the
        //     field is 0, on the "cold boot is unknown, not offline" rule
        //   • isServiceSideIssue() — needs it to tell "backend/React leg is
        //     down" apart from "the whole network is down"
        // Nothing ever called this, so the field stayed 0 for the life of the
        // process: `web_offline` was never published on any device, and the
        // overlay's cause line could never resolve to "Service issue — call
        // support" and always fell through to its default, "Can't reach
        // server". A kiosk sitting behind a stuck overlay therefore reported
        // pure green — native heartbeat flowing, ref_validation_status ok —
        // with nothing anywhere to say the UI was unusable.
        //
        // Deliberately after the response.ok check: this marks "React reached
        // the server", not "React ran". A tick that failed to POST must not
        // refresh the liveness clock, or the signal would report health for a
        // leg that is not actually reaching anything.
        try {
          if (typeof window.AndroidDevice?.markReactTelemetryAlive === 'function') {
            window.AndroidDevice.markReactTelemetryAlive();
          }
        } catch (e) {
          // Bridge is absent in a normal browser and may throw on an older
          // kiosk build — never let it break the telemetry loop.
          console.warn('[useTelemetry] markReactTelemetryAlive failed:', e?.message || e);
        }
      } catch (err) {
        console.error('[useTelemetry] ✗ Error sending telemetry:', err?.message || err);
      }
    };

    // Send immediately on mount
    sendTelemetry();
    setTelemetryState({ hasError: false, isRunning: true });

    // Set up interval for periodic sends
    intervalRef.current = setInterval(() => {
      sendTelemetry();
    }, intervalMs);

    // Page visibility handler (pause on hidden, resume on visible)
    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';
      console.log(`[useTelemetry] ${isVisibleRef.current ? '👀 Page visible' : '🚫 Page hidden'}`);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Browser close handler
    const handleBeforeUnload = () => {
      console.log('[useTelemetry] 🛑 Browser closing, stopping telemetry');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on unmount
    return () => {
      console.log('[useTelemetry] 🧹 Cleanup: stopping telemetry');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      setTelemetryState({ hasError: false, isRunning: false });
    };
  }, [serviceDeskCount, token, deviceId, intervalMs, hasError, config?.api_base]);

  return telemetryState;
}
